import { useState, useEffect } from 'react';
import { FaUserSecret, FaTimes, FaEye, FaLock } from 'react-icons/fa';
import saApi from '../../services/superAdminApi';
import toast from 'react-hot-toast';

const pad = (n) => String(n).padStart(2, '0');
const formatTime = (ms) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(s / 60);
  const h = Math.floor(m / 60);
  if (h > 0) return `${h}h ${pad(m % 60)}m`;
  return `${pad(m)}:${pad(s % 60)}`;
};

const cleanupImpersonation = () => {
  localStorage.removeItem('trainaxis_imp_session');
  localStorage.removeItem('trainaxis_token');
  localStorage.removeItem('trainaxis_user');
  localStorage.removeItem('trainaxis_club_slug');
};

export default function ImpersonationBanner() {
  const [ctx,       setCtx]       = useState(null);
  const [remaining, setRemaining] = useState(0);
  const [ending,    setEnding]    = useState(false);

  useEffect(() => {
    const raw = localStorage.getItem('trainaxis_imp_session');
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw);
      setCtx(parsed);
      setRemaining(new Date(parsed.expiresAt) - Date.now());
    } catch {
      localStorage.removeItem('trainaxis_imp_session');
    }
  }, []);

  useEffect(() => {
    if (!ctx) return;
    const id = setInterval(() => {
      const r = new Date(ctx.expiresAt) - Date.now();
      setRemaining(r);
      if (r <= 0) {
        cleanupImpersonation();
        window.location.replace('/superadmin/dashboard');
      }
    }, 1000);
    return () => clearInterval(id);
  }, [ctx]);

  const handleEnd = async () => {
    if (!window.confirm('End this impersonation session and return to the superadmin panel?')) return;
    setEnding(true);
    try {
      await saApi.post('/api/super-admin/impersonation/end', { sessionId: ctx.sessionId });
    } catch { /* best-effort — session will auto-expire */ }
    cleanupImpersonation();
    // Replace history entry so the back button doesn't re-enter the impersonated session
    window.location.replace('/superadmin/dashboard');
  };

  if (!ctx) return null;

  const isWarning  = remaining < 5 * 60 * 1000;
  const isReadOnly = ctx.mode === 'readonly';

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[9999] flex items-center justify-between gap-3 px-4 py-2 text-white text-sm font-medium shadow-lg"
      style={{ background: isWarning ? '#92400e' : '#7f1d1d', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
      {/* Left: identity */}
      <div className="flex items-center gap-2.5 min-w-0">
        <FaUserSecret className="flex-shrink-0 text-red-300" />
        <span className="font-bold text-red-200 text-xs uppercase tracking-wider">Impersonating</span>
        <span className="font-semibold truncate">{ctx.clubName}</span>
        <span
          className="flex-shrink-0 flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full"
          style={{ background: isReadOnly ? 'rgba(0,0,0,0.3)' : 'rgba(239,68,68,0.4)', color: isReadOnly ? '#fca5a5' : '#fecaca' }}>
          {isReadOnly ? <FaEye className="text-xs" /> : <FaLock className="text-xs" />}
          {isReadOnly ? 'Read-Only' : 'Full Access'}
        </span>
        {ctx.reason && (
          <span className="hidden sm:inline text-xs text-red-300 truncate max-w-[240px]" title={ctx.reason}>
            · {ctx.reason}
          </span>
        )}
      </div>

      {/* Right: timer + exit */}
      <div className="flex items-center gap-3 flex-shrink-0">
        <span className={`font-mono text-sm tabular-nums ${isWarning ? 'text-yellow-300 font-bold' : 'text-red-200'}`}>
          {formatTime(remaining)}
        </span>
        <button
          onClick={handleEnd}
          disabled={ending}
          className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg transition-all hover:bg-white/20 active:scale-95"
          style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.2)' }}>
          <FaTimes className="text-xs" />
          {ending ? 'Ending…' : 'Exit Session'}
        </button>
      </div>
    </div>
  );
}
